import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { productionKitEndpoints } from '@/lib/api/endpoints';
import { productionKitKeys } from '@/lib/queryKeys';
import type { ProductionKit, ProductionKitInput } from '@/types/costing';

export function useProductionKits() {
  return useApiQuery<ProductionKit[]>({
    queryKey: productionKitKeys.list(),
    queryFn: () => apiGet<ProductionKit[]>(operationsClient, productionKitEndpoints.list()),
    showErrorToast: false,
  });
}

export function useCreateProductionKit() {
  const qc = useQueryClient();
  return useApiMutation<ProductionKit, ProductionKitInput>({
    mutationFn: (dto) => apiPost(operationsClient, productionKitEndpoints.create(), dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: productionKitKeys.all() });
      toast.success('Production kit created');
    },
  });
}

export function useUpdateProductionKit() {
  const qc = useQueryClient();
  return useApiMutation<ProductionKit, { id: string; patch: Partial<ProductionKitInput> }>({
    mutationFn: ({ id, patch }) =>
      apiPatch(operationsClient, productionKitEndpoints.update(id), patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: productionKitKeys.all() });
      toast.success('Production kit updated');
    },
  });
}

export function useDeleteProductionKit() {
  const qc = useQueryClient();
  return useApiMutation<{ id: string }, string>({
    mutationFn: (id) => apiDelete(operationsClient, productionKitEndpoints.remove(id)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: productionKitKeys.all() });
      toast.success('Production kit deleted');
    },
  });
}
